<script>
  /**
   * Auto-responder settings for the current tenant, plus the send gate.
   *
   * The gate is the part worth reading carefully. It has two halves — a
   * process-wide env var and a per-tenant flag — and this screen can only
   * change the second. So it always shows the *resolved* outcome from the
   * server rather than implying that flipping the switch is sufficient.
   */
  import api from '$lib/api.js';
  import { toast } from '$lib/toast.svelte.js';
  import { authStore } from '../stores/authStore.svelte.js';
  import PageHeader from '../components/PageHeader.svelte';
  import ConfirmModal from '../components/ConfirmModal.svelte';

  let loading = $state(true);
  let error = $state(null);
  let config = $state(null);
  let form = $state({});
  let saving = $state(false);
  let confirmRealSends = $state(false);
  let realSendsReason = $state('');
  let togglingSends = $state(false);

  const tenantId = $derived(authStore.tenantId ?? undefined);

  const GATE_STYLE = {
    REAL: { bg: 'var(--p-danger-bg)', fg: 'var(--p-danger)', icon: 'fa-tower-broadcast' },
    MOCK: { bg: 'var(--p-info-bg)', fg: 'var(--p-info)', icon: 'fa-flask' },
    REFUSE: { bg: 'var(--p-bg-subtle, rgba(120,120,140,.1))', fg: 'var(--p-text-muted)', icon: 'fa-shield-halved' },
  };
  const gateStyle = $derived(GATE_STYLE[config?.send_gate?.mode] ?? GATE_STYLE.REFUSE);

  async function load() {
    loading = true;
    error = null;
    try {
      config = await api.getSettings({ tenant_id: tenantId });
      form = {
        enabled: config.enabled,
        confidence_threshold: config.confidence_threshold,
        response_delay_minutes: config.response_delay_minutes,
        max_auto_responses_per_day: config.max_auto_responses_per_day,
        max_llm_responses_per_day: config.max_llm_responses_per_day,
        escalation_email: config.escalation_email ?? '',
        business_hours_enabled: config.business_hours_enabled,
        business_hours_start: config.business_hours_start ?? '',
        business_hours_end: config.business_hours_end ?? '',
        business_timezone: config.business_timezone ?? '',
        emergency_keywords: (config.emergency_keywords ?? []).join(', '),
        emergency_escalation_enabled: config.emergency_escalation_enabled,
        notification_rate_limit_per_hour: config.notification_rate_limit_per_hour,
      };
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  $effect(() => { tenantId; load(); });

  async function save(event) {
    event.preventDefault();
    saving = true;
    try {
      config = await api.updateSettings({
        ...form,
        escalation_email: form.escalation_email || null,
        business_hours_start: form.business_hours_start || null,
        business_hours_end: form.business_hours_end || null,
        business_timezone: form.business_timezone || null,
        emergency_keywords: form.emergency_keywords
          .split(',').map((k) => k.trim()).filter(Boolean),
        confidence_threshold: Number(form.confidence_threshold),
        response_delay_minutes: Number(form.response_delay_minutes),
        max_auto_responses_per_day: Number(form.max_auto_responses_per_day),
        max_llm_responses_per_day: Number(form.max_llm_responses_per_day),
        notification_rate_limit_per_hour: Number(form.notification_rate_limit_per_hour),
      }, { tenant_id: tenantId });
      toast.success('Settings saved.');
    } catch (e) {
      toast.error(e.message);
    } finally {
      saving = false;
    }
  }

  async function applyRealSends(allow) {
    togglingSends = true;
    try {
      config = await api.setRealSends(allow, realSendsReason || null, { tenant_id: tenantId });
      confirmRealSends = false;
      realSendsReason = '';
      // Report the resolved mode, not the switch position — they differ
      // whenever the server-side env half disagrees.
      if (config.send_gate.mode === 'REAL') toast.success('Real sends are now ON for this tenant.');
      else toast.info(`Saved, but nothing will reach a guest: ${config.send_gate.explanation}`);
    } catch (e) {
      toast.error(e.message);
    } finally {
      togglingSends = false;
    }
  }
</script>

<PageHeader title="Auto-responder settings"
            subtitle="How this tenant's replies are drafted, and whether they're delivered." />

{#if loading}
  <p class="p-card p-6 text-sm p-muted">Loading…</p>
{:else if error}
  <p class="p-card p-4 text-sm" style="color: var(--p-danger)">{error}</p>
{:else if config}
  <section class="p-card mb-6 p-4">
    <h2 class="text-sm font-semibold">Guest message delivery</h2>
    <p class="mt-3 flex items-start gap-2 rounded-lg px-3 py-2 text-sm"
       style="background-color: {gateStyle.bg}; color: {gateStyle.fg}">
      <i class="fas {gateStyle.icon} mt-0.5" aria-hidden="true"></i>
      <span>
        <strong>{config.send_gate.mode}</strong> — {config.send_gate.explanation}
      </span>
    </p>

    <dl class="mt-3 grid gap-2 text-xs sm:grid-cols-3">
      <div>
        <dt class="p-faint">Server mock mode</dt>
        <dd>{config.send_gate.mock_mode_env ? 'on' : 'off'}</dd>
      </div>
      <div>
        <dt class="p-faint">Server real sends</dt>
        <dd>{config.send_gate.allow_real_sends_env ? 'allowed' : 'not allowed'}</dd>
      </div>
      <div>
        <dt class="p-faint">This tenant opted in</dt>
        <dd>{config.send_gate.tenant_allow_real_sends ? 'yes' : 'no'}</dd>
      </div>
    </dl>
    <p class="mt-2 text-xs p-faint">
      The first two are server settings and can only be changed by a deployment, not from here.
    </p>

    {#if authStore.isSuperUser}
      <div class="mt-4">
        {#if config.send_gate.tenant_allow_real_sends}
          <button type="button" class="p-btn p-btn-quiet" disabled={togglingSends}
                  onclick={() => applyRealSends(false)}>
            Turn off real sends for this tenant
          </button>
        {:else}
          <button type="button" class="p-btn p-btn-primary" disabled={togglingSends}
                  onclick={() => (confirmRealSends = true)}>
            Turn on real sends for this tenant
          </button>
        {/if}
      </div>
    {:else}
      <p class="mt-4 text-xs p-faint">Only a portal administrator can change this.</p>
    {/if}
  </section>

  <form class="p-card space-y-5 p-4" onsubmit={save}>
    <label class="flex items-center gap-2 text-sm">
      <input type="checkbox" bind:checked={form.enabled} />
      Auto-responder enabled
    </label>

    <div class="grid gap-4 sm:grid-cols-2">
      <div>
        <label for="conf" class="text-xs font-medium p-muted">
          Confidence threshold ({form.confidence_threshold})
        </label>
        <input id="conf" class="mt-2 w-full" type="range" min="0" max="1" step="0.01"
               bind:value={form.confidence_threshold} />
        <p class="mt-1 text-xs p-faint">
          Drafts below this are held for review instead of sent automatically.
        </p>
      </div>
      <div>
        <label for="delay" class="text-xs font-medium p-muted">Response delay (minutes)</label>
        <input id="delay" class="p-input mt-1" type="number" min="0" max="1440"
               bind:value={form.response_delay_minutes} />
      </div>
      <div>
        <label for="cap" class="text-xs font-medium p-muted">Max auto-responses per day</label>
        <input id="cap" class="p-input mt-1" type="number" min="0"
               bind:value={form.max_auto_responses_per_day} />
      </div>
      <div>
        <label for="llmcap" class="text-xs font-medium p-muted">Max LLM responses per day</label>
        <input id="llmcap" class="p-input mt-1" type="number" min="0"
               bind:value={form.max_llm_responses_per_day} />
      </div>
    </div>

    <div>
      <label class="flex items-center gap-2 text-sm">
        <input type="checkbox" bind:checked={form.business_hours_enabled} />
        Only reply during business hours
      </label>
      {#if form.business_hours_enabled}
        <div class="mt-3 grid gap-4 sm:grid-cols-3">
          <div>
            <label for="bhs" class="text-xs font-medium p-muted">Start</label>
            <input id="bhs" class="p-input mt-1" type="time" bind:value={form.business_hours_start} />
          </div>
          <div>
            <label for="bhe" class="text-xs font-medium p-muted">End</label>
            <input id="bhe" class="p-input mt-1" type="time" bind:value={form.business_hours_end} />
          </div>
          <div>
            <label for="tz" class="text-xs font-medium p-muted">Timezone</label>
            <input id="tz" class="p-input mt-1" bind:value={form.business_timezone}
                   placeholder="America/New_York" />
          </div>
        </div>
      {/if}
    </div>

    <div>
      <label for="kw" class="text-xs font-medium p-muted">Emergency keywords</label>
      <input id="kw" class="p-input mt-1" bind:value={form.emergency_keywords}
             placeholder="fire, flood, broken" />
      <p class="mt-1 text-xs p-faint">
        Comma-separated. A message matching any of these skips drafting entirely and is
        escalated with no reply written.
      </p>
    </div>

    <div class="grid gap-4 sm:grid-cols-2">
      <div>
        <label for="esc" class="text-xs font-medium p-muted">Escalation email</label>
        <input id="esc" class="p-input mt-1" type="email" bind:value={form.escalation_email} />
      </div>
      <div>
        <label for="rate" class="text-xs font-medium p-muted">Notifications per hour</label>
        <input id="rate" class="p-input mt-1" type="number" min="0"
               bind:value={form.notification_rate_limit_per_hour} />
      </div>
    </div>

    <label class="flex items-center gap-2 text-sm">
      <input type="checkbox" bind:checked={form.emergency_escalation_enabled} />
      Escalate emergencies immediately
    </label>

    <div class="flex flex-wrap items-center justify-between gap-3 border-t pt-4"
         style="border-color: var(--p-border)">
      <p class="text-xs p-faint">
        Notification channels:
        {config.telegram_configured ? 'Telegram' : ''}
        {config.slack_configured ? 'Slack' : ''}
        {config.sms_configured ? 'SMS' : ''}
        {!config.telegram_configured && !config.slack_configured && !config.sms_configured
          ? 'none configured on the server' : ''}
      </p>
      <button type="submit" class="p-btn p-btn-primary" disabled={saving}>Save settings</button>
    </div>
  </form>
{/if}

<ConfirmModal open={confirmRealSends}
              title="Send real messages to guests?"
              message="Approved replies for this tenant will be delivered to real guests through Hostaway. This is recorded in the audit log."
              confirmLabel="Turn on real sends"
              confirmDisabled={!realSendsReason.trim() || togglingSends}
              onConfirm={() => applyRealSends(true)}
              onCancel={() => { confirmRealSends = false; realSendsReason = ''; }}>
  {#snippet children()}
    <label for="reason" class="mt-4 block text-xs font-medium p-muted">
      Why are you enabling this? (required)
    </label>
    <input id="reason" class="p-input mt-1" bind:value={realSendsReason}
           placeholder="e.g. pilot tenant going live" />
  {/snippet}
</ConfirmModal>
